'use client'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import {
  stacks,
  RESOURCES,
  DEFAULT_SETTINGS,
  GAME_MODES,
  DISPLAY_MODES,
} from './stack-config'
import {
  getCardDisplay,
  shuffleArray,
  getShakeAnimationStyle,
  saveSettingsToStorage,
  loadSettingsFromStorage,
} from './card-utils'
import { StackSettings, Card, TempSettings, Stack } from './types'
import { SettingsModal } from './SettingsModal'
import { StackViewModal } from './StackViewModal'
import { validateSettings, processValidSettings } from './validation'

// Destructure resource paths for easier access
const { CARD_CDN, NUMBER_CDN, ANSWER_URL, STORAGE_KEY } = RESOURCES

/**
 * Client-side component for the Stack Trainer application
 */
export const StackTrainerClient = () => {
  /********************************************
   * STATE DEFINITIONS
   ********************************************/

  // Settings state with defaults
  const [mode, setMode] = useState(DEFAULT_SETTINGS.mode)
  const [stackStart, setStackStart] = useState(DEFAULT_SETTINGS.stackStart)
  const [stackEnd, setStackEnd] = useState(DEFAULT_SETTINGS.stackEnd)
  const [timerSeconds, setTimerSeconds] = useState(
    DEFAULT_SETTINGS.timerSeconds
  )
  const [selectedStackName, setSelectedStackName] = useState(
    DEFAULT_SETTINGS.selectedStackName
  )
  const [displayMode, setDisplayMode] = useState(DEFAULT_SETTINGS.displayMode)

  // Gameplay statistics
  const [totalAnswers, setTotalAnswers] = useState(0)
  const [correctAnswers, setCorrectAnswers] = useState(0)

  // Game state
  const [stack, setStack] = useState<Card[]>([])
  const [shuffledStack, setShuffledStack] = useState<Card[]>([])
  const [currentCardPosition, setCurrentCardPosition] = useState(-1)
  const [correctAnswerButton, setCorrectAnswerButton] = useState(0)
  const [answerOptions, setAnswerOptions] = useState<
    (string | React.ReactNode)[]
  >(['', '', ''])
  const [answerStatuses, setAnswerStatuses] = useState<string[]>([
    'default',
    'default',
    'default',
  ])
  const [freezeAnswers, setFreezeAnswers] = useState(false)

  // Display state
  const [cardImageSrc, setCardImageSrc] = useState(ANSWER_URL)
  const [answerCardSrc, setAnswerCardSrc] = useState(ANSWER_URL)
  const [correctAnswerImage, setCorrectAnswerImage] = useState('')
  const [timerProgress, setTimerProgress] = useState(0)

  // Modal visibility
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [showCurrentStackModal, setShowCurrentStackModal] = useState(false)

  // Temporary settings for the modal
  const [tempSettings, setTempSettings] = useState<TempSettings>({
    tempMode: mode,
    tempStackStart: stackStart,
    tempStackEnd: stackEnd,
    tempTimerSeconds: timerSeconds,
    tempSelectedStackName: selectedStackName,
    tempDisplayMode: displayMode,
  })

  /********************************************
   * REFS
   ********************************************/

  // Refs for managing timers and state between renders
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastIndexRef = useRef<number>(-1)
  const correctAnswerImageRef = useRef<string>('')
  const settingsInitializedRef = useRef<boolean>(false)
  const placeNextCardRef = useRef<(() => void) | null>(null)
  const stackRangeRef = useRef({
    stackStart,
    stackEnd,
  })

  /********************************************
   * MEMOIZED VALUES
   ********************************************/

  // Create a memoized stack details text to avoid recalculation on each render
  const stackDetailsText = useMemo(
    () => `${selectedStackName} (cards ${stackStart} to ${stackEnd})`,
    [selectedStackName, stackStart, stackEnd]
  )

  const getShuffleStack = useCallback(() => {
    const { stackStart, stackEnd } = stackRangeRef.current
    const subStack = stack.slice(stackStart - 1, stackEnd)
    return shuffleArray(subStack)
  }, [stack])

  /********************************************
   * EFFECT HOOKS
   ********************************************/

  // Update correctAnswerImageRef when correctAnswerImage changes
  useEffect(() => {
    correctAnswerImageRef.current = correctAnswerImage
  }, [correctAnswerImage])

  useEffect(() => {
    stackRangeRef.current = {
      stackStart,
      stackEnd,
    }
  }, [stackStart, stackEnd])

  // Load settings from localStorage on initial render
  useEffect(() => {
    if (!settingsInitializedRef.current) {
      const storedSettings = loadSettingsFromStorage(
        STORAGE_KEY
      ) as StackSettings | null
      if (storedSettings) {
        queueMicrotask(() => {
          setMode(storedSettings.mode)
          setStackStart(storedSettings.stackStart)
          setStackEnd(storedSettings.stackEnd)
          setTimerSeconds(storedSettings.timerSeconds)
          setSelectedStackName(storedSettings.selectedStackName)
          setDisplayMode(storedSettings.displayMode)
        })
      }
      settingsInitializedRef.current = true
    }
  }, [])

  // Update temp settings when actual settings change
  useEffect(() => {
    queueMicrotask(() => {
      setTempSettings({
        tempMode: mode,
        tempStackStart: stackStart,
        tempStackEnd: stackEnd,
        tempTimerSeconds: timerSeconds,
        tempSelectedStackName: selectedStackName,
        tempDisplayMode: displayMode,
      })
    })
  }, [mode, stackStart, stackEnd, timerSeconds, selectedStackName, displayMode])

  // Save settings to localStorage whenever they change
  useEffect(() => {
    if (settingsInitializedRef.current) {
      const settings = {
        mode,
        stackStart,
        stackEnd,
        timerSeconds,
        selectedStackName,
        displayMode,
      }
      saveSettingsToStorage(STORAGE_KEY, settings)
    }
  }, [mode, stackStart, stackEnd, timerSeconds, selectedStackName, displayMode])

  // Update shuffled stack and start the game when stack changes
  useEffect(() => {
    if (stack.length === 0) return
    queueMicrotask(() => {
      const newShuffled = getShuffleStack()
      setShuffledStack(newShuffled)
      setCurrentCardPosition(-1)
      placeNextCardRef.current?.()
    })
  }, [stack, getShuffleStack])

  // Ensure all timers are cleared on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current !== null) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [])

  /********************************************
   * CALLBACK FUNCTIONS
   ********************************************/

  /**
   * Populates the stack array based on the selected stack name
   */
  const populateStack = useCallback(() => {
    // Provide a default stack if the selected one is not found
    const selected = stacks.find(s => s.name === selectedStackName) as
      | Stack
      | undefined
    if (!selected) {
      console.warn(
        `Stack "${selectedStackName}" not found, using default stack`
      )
      // Use the first stack as a fallback
      const defaultStack: Card[] = stacks[0]
        ? stacks[0].order.split(',').map((value, index) => ({
            code: value.trim(),
            index: index + 1,
          }))
        : []

      setStack(defaultStack)
      return
    }

    const newStack: Card[] = selected.order.split(',').map((value, index) => ({
      code: value.trim(),
      index: index + 1,
    }))

    setStack(newStack)
  }, [selectedStackName])

  // Populate the stack when selectedStackName, stackStart, or stackEnd changes
  useEffect(() => {
    queueMicrotask(() => {
      populateStack()
    })
  }, [selectedStackName, stackStart, stackEnd, populateStack])

  /**
   * Resets the view to a default state
   */
  const resetView = useCallback(() => {
    setAnswerCardSrc(ANSWER_URL)
    setAnswerStatuses(['default', 'default', 'default'])
  }, [])

  /**
   * Generates random answer options for the current card
   * @param {number} currentIndex - The index of the current card
   * @param {string} currentAnswerMode - The current answer mode (guess-card or guess-position)
   * @param {Card[]} currentDeck - The current deck of cards
   */
  const generateRandomAnswers = useCallback(
    (currentIndex: number, currentAnswerMode: string, currentDeck: Card[]) => {
      // Validate inputs to prevent runtime errors
      if (currentIndex < 0 || currentIndex >= currentDeck.length) {
        console.error('Invalid currentIndex in generateRandomAnswers')
        return
      }

      // Ensure we have enough cards for generating options
      if (currentDeck.length < 2) {
        console.warn('Not enough cards to generate random answers')
        // For a single card, just show that card as the only option
        setCorrectAnswerButton(0)
        const currentCard = currentDeck[0]
        const correctAnswer =
          currentAnswerMode === GAME_MODES.GUESS_CARD
            ? getCardDisplay(currentCard.code, 'button')
            : currentCard.index.toString()
        setAnswerOptions([correctAnswer, correctAnswer, correctAnswer])
        return
      }

      // Create a copy of the deck and remove the current card
      const tempDeck = [...currentDeck]
      tempDeck.splice(currentIndex, 1)

      // Shuffle the remaining cards for options
      const answerDeck = shuffleArray(tempDeck)

      // Randomly choose where to put the correct answer
      const correctIndex = Math.floor(Math.random() * 3)
      setCorrectAnswerButton(correctIndex)

      // Get the current card and determine the correct answer
      const currentCard = currentDeck[currentIndex]
      const correctAnswer =
        currentAnswerMode === GAME_MODES.GUESS_CARD
          ? getCardDisplay(currentCard.code, 'button')
          : currentCard.index.toString()

      // Generate the answer options
      const answers = Array(3).fill('')
      for (let i = 0; i < 3; i++) {
        if (i === correctIndex) {
          answers[i] = correctAnswer
        } else {
          // Ensure we have a valid index for answerDeck
          const optionIndex = i < correctIndex ? i : i - 1
          const option =
            optionIndex < answerDeck.length
              ? answerDeck[optionIndex]
              : answerDeck[0] // Fallback to the first option if out of bounds

          const answerOption =
            currentAnswerMode === GAME_MODES.GUESS_CARD
              ? getCardDisplay(option.code, 'button')
              : option.index.toString()
          answers[i] = answerOption
        }
      }

      setAnswerOptions(answers)
    },
    []
  )

  /**
   * Starts a timer for the current question
   */
  const startTimer = useCallback(() => {
    // Clear any existing timer
    if (timerIntervalRef.current !== null) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    // Reset the timer progress
    setTimerProgress(0)

    // Store the time when timer was started to prevent drift
    const startTime = Date.now()
    const endTime = startTime + timerSeconds * 1000

    // Start a new timer
    timerIntervalRef.current = setInterval(() => {
      const currentTime = Date.now()
      const elapsedSeconds = Math.floor((currentTime - startTime) / 1000)

      // Use the actual elapsed time to calculate progress
      setTimerProgress(elapsedSeconds)

      // Check if timer is complete based on actual time
      if (currentTime >= endTime) {
        // Stop the timer
        if (timerIntervalRef.current !== null) {
          clearInterval(timerIntervalRef.current)
          timerIntervalRef.current = null
        }

        // Use functional updates to avoid stale closure issues
        setFreezeAnswers(currentFreezeAnswers => {
          if (!currentFreezeAnswers) {
            // Handle auto vs. multiple choice display
            if (displayMode === DISPLAY_MODES.AUTO) {
              // Show the answer and move to the next card after delay
              setAnswerCardSrc(correctAnswerImageRef.current)

              setTimeout(() => {
                if (placeNextCardRef.current) {
                  placeNextCardRef.current()
                }
              }, 2000)
            } else {
              // Mark as incorrect and move to the next card after delay
              setAnswerStatuses(prevStatuses => {
                const newStatuses = [...prevStatuses]
                newStatuses[correctAnswerButton] = 'incorrect'
                return newStatuses
              })
              setTotalAnswers(prev => prev + 1)

              setTimeout(() => {
                if (placeNextCardRef.current) {
                  placeNextCardRef.current()
                }
              }, 3000)
            }
            
            return true // Set freezeAnswers to true
          }
          return currentFreezeAnswers // Don't change if already frozen
        })
      }
    }, 100) // More frequent updates for accuracy

    // Return a cleanup function
    return () => {
      if (timerIntervalRef.current !== null) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [timerSeconds, displayMode, correctAnswerButton])

  /**
   * Places the next card for guessing
   */
  const placeNextCard = useCallback(() => {
    // Clear any existing timers
    if (timerIntervalRef.current !== null) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    // Get the deck to use
    let deck: Card[] =
      shuffledStack.length > 0 ? shuffledStack : getShuffleStack()
    if (shuffledStack.length === 0) {
      setShuffledStack(deck)
    }

    // Determine the next card index
    let newIndex
    if (mode === GAME_MODES.RANDOM) {
      if (deck.length > 1) {
        // Ensure we don't get the same card twice in a row
        do {
          newIndex = Math.floor(Math.random() * deck.length)
        } while (newIndex === lastIndexRef.current)
      } else {
        newIndex = 0
      }
      lastIndexRef.current = newIndex
    } else {
      // Sequential mode - get the next card
      newIndex = currentCardPosition + 1
      if (newIndex >= deck.length) {
        // Start over with a new shuffled deck
        deck = getShuffleStack()
        setShuffledStack(deck)
        newIndex = 0
      }
    }

    setCurrentCardPosition(newIndex)
    resetView()

    // Get the current card and set up the display
    const currentCard = deck[newIndex]

    // Determine the answer mode (what to guess)
    const effectiveMode = mode === GAME_MODES.RANDOM ? undefined : mode
    const newAnswerMode =
      effectiveMode ||
      (Math.random() < 0.5 ? GAME_MODES.GUESS_CARD : GAME_MODES.GUESS_POSITION)

    // Set up the right images based on the mode
    if (newAnswerMode === GAME_MODES.GUESS_POSITION) {
      // Show card, guess position
      setCardImageSrc(`${CARD_CDN}${currentCard.code.toLowerCase()}.png`)
      setCorrectAnswerImage(`${NUMBER_CDN}${currentCard.index}.png`)
    } else if (newAnswerMode === GAME_MODES.GUESS_CARD) {
      // Show position, guess card
      setCardImageSrc(`${NUMBER_CDN}${currentCard.index}.png`)
      setCorrectAnswerImage(`${CARD_CDN}${currentCard.code.toLowerCase()}.png`)
    }

    setFreezeAnswers(false)

    // Only generate answer options for multiple choice mode
    if (displayMode === DISPLAY_MODES.MULTIPLE) {
      generateRandomAnswers(newIndex, newAnswerMode, deck)
    }

    // Start timer if needed
    if (timerSeconds > 0) {
      startTimer()
    }
  }, [
    mode,
    currentCardPosition,
    shuffledStack,
    getShuffleStack,
    timerSeconds,
    generateRandomAnswers,
    startTimer,
    resetView,
    displayMode,
  ])

  // Update placeNextCardRef when placeNextCard changes
  useEffect(() => {
    placeNextCardRef.current = placeNextCard
  }, [placeNextCard])

  /**
   * Handles a user's answer selection
   * @param {number} buttonIndex - The index of the selected button (0-2)
   * @param {React.MouseEvent} event - The click event
   */
  const handleAnswer = useCallback(
    (buttonIndex: number, event: React.MouseEvent) => {
      event.preventDefault()

      // Do nothing if answers are frozen
      if (freezeAnswers) return

      setFreezeAnswers(true)
      setTotalAnswers(prev => prev + 1)

      // Clear timer if it's running
      if (timerSeconds > 0 && timerIntervalRef.current !== null) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }

      if (buttonIndex === correctAnswerButton) {
        // Correct answer selected
        setAnswerStatuses(prev => {
          const newStatuses = [...prev]
          newStatuses[buttonIndex] = 'correct'
          return newStatuses
        })
        setCorrectAnswers(prev => prev + 1)
      } else {
        // Wrong answer selected
        // First mark the selected (wrong) button as incorrect
        setAnswerStatuses(prev => {
          const newStatuses = [...prev]
          newStatuses[buttonIndex] = 'incorrect'
          return newStatuses
        })

        // Then after a short delay, apply the shake animation to the correct button
        const correctBtn = correctAnswerButton
        setTimeout(() => {
          setAnswerStatuses(prev => {
            const newStatuses = [...prev]
            newStatuses[correctBtn] = 'shake'
            return newStatuses
          })
        }, 400)
      }

      // Move to next card after delay
      const nextCardTimer = setTimeout(() => {
        if (placeNextCardRef.current) {
          placeNextCardRef.current()
        }
      }, 3000)

      // Return cleanup function for the component's useEffect
      return () => clearTimeout(nextCardTimer)
    },
    [freezeAnswers, timerSeconds, correctAnswerButton]
  )

  /**
   * Handles saving the settings from the modal
   */
  const handleSettingsSave = useCallback(() => {
    const validationError = validateSettings(tempSettings)
    if (validationError) {
      alert(validationError)
      return
    }

    const processedSettings = processValidSettings(tempSettings)
    
    // Apply settings
    setMode(processedSettings.mode)
    setStackStart(processedSettings.stackStart)
    setStackEnd(processedSettings.stackEnd)
    setTimerSeconds(processedSettings.timerSeconds)
    setSelectedStackName(processedSettings.selectedStackName)
    setDisplayMode(processedSettings.displayMode)

    // Update the game state
    populateStack()
    setShuffledStack(getShuffleStack())
    setCurrentCardPosition(-1)
    placeNextCard()
    setFreezeAnswers(false)

    // Close the modal
    setShowSettingsModal(false)
  }, [tempSettings, populateStack, getShuffleStack, placeNextCard])

  /********************************************
   * RENDERING
   ********************************************/

  // Style for answer buttons
  const baseClass = 'flex-1 py-2 px-4 text-lg font-bold rounded'

  return (
    <>
      <style>{getShakeAnimationStyle()}</style>
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <h1 className="text-2xl font-bold mb-4">
          Vanishing Inc. Stack Trainer
        </h1>
        <p className="text-lg mb-4">
          Current stack: <span>{stackDetailsText}</span>{' '}
          <button
            className="text-vi-red underline hover:underline cursor-pointer"
            onClick={() => setShowCurrentStackModal(true)}
            aria-label="View current stack details"
          >
            View
          </button>
        </p>

        {/* Card Display */}
        <div className="text-center">
          {displayMode === DISPLAY_MODES.AUTO ? (
            <div className="flex justify-center items-center">
              <img
                className="mx-4"
                id="questionImage"
                src={cardImageSrc}
                alt="Question Card"
                aria-label="Question Card"
              />
              <img
                className="mx-4"
                id="answerImage"
                src={answerCardSrc}
                alt="Answer Card"
                aria-label="Answer Card"
              />
            </div>
          ) : (
            <div className="flex justify-center items-center">
              <img
                className="mx-4"
                id="card"
                src={cardImageSrc}
                alt="Card"
                aria-label="Card to guess"
              />
            </div>
          )}
        </div>

        {/* Answer Buttons (Multiple Choice Mode) */}
        {displayMode === DISPLAY_MODES.MULTIPLE && (
          <div
            id="answer-buttons"
            className="mt-4 flex space-x-2"
            role="region"
            aria-label="Answer choices"
          >
            {answerOptions.map((option, index) => (
              <button
                key={index}
                onClick={e => handleAnswer(index, e)}
                disabled={freezeAnswers}
                aria-pressed={answerStatuses[index] !== 'default'}
                className={`${baseClass} ${
                  answerStatuses[index] === 'default'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : answerStatuses[index] === 'correct'
                      ? 'bg-green-600 text-white'
                      : answerStatuses[index] === 'incorrect'
                        ? 'bg-red-600 text-white'
                        : answerStatuses[index] === 'shake'
                          ? 'bg-blue-600 text-white shake-animation'
                          : 'bg-blue-600 text-white'
                }`}
              >
                <span className="mr-2">{option}</span>
                {answerStatuses[index] === 'correct' && (
                  <CheckIcon
                    className="w-6 h-6 inline float-right"
                    aria-hidden="true"
                  />
                )}
                {answerStatuses[index] === 'incorrect' && (
                  <XMarkIcon
                    className="w-6 h-6 inline float-right"
                    aria-hidden="true"
                  />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Timer and Results */}
        <div className="mt-3">
          {timerSeconds > 0 && (
            <>
              <progress
                id="timer"
                value={timerProgress}
                max={timerSeconds}
                className="w-full h-1"
                aria-label={`Time remaining: ${timerSeconds - timerProgress} seconds`}
              ></progress>
              <p className="text-sm text-gray-600 text-right mt-1 pb-3">
                Time remaining: {timerSeconds - timerProgress} sec
              </p>
            </>
          )}
          <div
            className="text-gray-500 pt-3 flex justify-between border-t"
            id="results"
          >
            <button
              className="text-vi-red underline hover:underline cursor-pointer"
              onClick={() => setShowSettingsModal(true)}
              aria-label="Open settings"
            >
              Settings
            </button>
            {displayMode === DISPLAY_MODES.MULTIPLE && (
              <span>
                {correctAnswers} correct out of {totalAnswers}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        tempSettings={tempSettings}
        onTempSettingsChange={setTempSettings}
        onSave={handleSettingsSave}
      />

      {/* Current Stack Modal */}
      <StackViewModal
        isOpen={showCurrentStackModal}
        onClose={() => setShowCurrentStackModal(false)}
        selectedStackName={selectedStackName}
      />
    </>
  )
}
